/* eslint react/prop-types: 0 */
import React from 'react';
import MultiSelect from 'components/ui/inputs/MultiSelect';
import Questionnaire from 'components/shared/Questionnaire';
import {
  activateLoader,
  deactivateLoader,
  getCapabilitiesForFunction,
} from 'utilities';
import { getCsrfToken } from 'utilities/http';
import { ANALYTICS } from 'consts';
import FunctionsCapabilitiesContext from 'components/shared/FunctionsCapabilitiesContext';
import FunctionsCapabilitiesProvider from 'components/shared/FunctionsCapabilitiesProvider';
import TalentSuperheroFunctions from 'components/shared/TalentSuperheroFunctions';
import TalentCapabilities from 'components/shared/TalentCapabilities';
import OtherCapabilities from 'components/shared/OtherCapabilities';
import Button from 'components/ui/buttons/Button';
import ButtonLink from 'components/ui/buttons/Button';
import { TalentFlexWrapper } from 'components/shared/styles';

const employment_status = {
  label: 'Which type of roles are you open to?*',
  fieldType: 'radio',
  fieldName: 'employment_status',
  options: {
    contract_without_benefits: 'Contract without benefits',
    contract_with_benefits: {
      label: 'Contract with benefits',
      description:
        'Many contract roles offer typical benefits such as health insurance, retirement benefits, and more. Access to benefits may be subject to a waiting period and have associated costs.',
    },
    parttime: 'Contract to permanent',
    fulltime: 'Permanent employee',
  },
};
const ID_PREPEND = `id-`;
const maxTotalCapabilities = 6;
const maxPrimaryCapabilities = 3;
const maxOtherCapabilities = 3;

class BidTalentDetail extends React.Component {
  constructor(props) {
    super(props);
    const { capabilities, selected_capabilities, selected_function } = props;
    this.state = {
      primaryFunction: selected_function,
      capabilities: getCapabilitiesForFunction(
        selected_function,
        capabilities,
        selected_capabilities,
        true,
      ),
      capabilities_filter: '',
      capabilities_other: getCapabilitiesForFunction(
        selected_function,
        capabilities,
        selected_capabilities,
      ),
      capabilities_other_filter: '',
      industries: Array.isArray(this.props.selected_industries)
        ? this.props.selected_industries
        : [],
      industries_filter: '',
      employment_status: this.props.employment_status.filter(n => n),
      phone: this.props.phone != null ? this.props.phone : '',
      zip: this.props.zip != null ? this.props.zip : '',
      valid: false,
      saving: false,
      status: this.props.bid_status,
    };
  }

  onChange = (key, value) => {
    this.setState({
      [key]: value,
    });
  };

  onChangeFunctionsCapabilities = newValues => {
    this.setState(newValues);
  };

  getIndustries = () =>
    this.props.industries.reduce((obj, row) => {
      obj[`${ID_PREPEND}${row.id}`] = row.name;
      return obj;
    }, {});

  valueAccessor = (obj, key) => {
    const prependedKey = key.toString().startsWith(ID_PREPEND)
      ? key
      : `${ID_PREPEND}${key}`;
    return obj[prependedKey];
  };

  keysAccessor = obj =>
    Object.keys(obj).map(key => key.replace(ID_PREPEND, ''));

  submitBid(draft) {
    return window.Bid.submitBid(draft);
  }

  willRemoveResume() {
    return window.Bid.willRemoveResume();
  }

  handleSubmit(draft = false) {
    if (!draft && !this.valid()) {
      return false;
    }
    activateLoader();

    const { state } = this;

    const roleTypes = {
      full_time: state.employment_status.includes('fulltime') ? true : false,
      contract_without_benefits: state.employment_status.includes(
        'contract_without_benefits',
      )
        ? true
        : false,
      contract_with_benefits: state.employment_status.includes(
        'contract_with_benefits',
      )
        ? true
        : false,
      maternityship: state.employment_status.includes('parttime')
        ? true
        : false,
    };

    const mom = {
      phone: state.phone.replace(/\D/g, '').replace(/^1/, ''),
      zip: state.zip,
      capability_ids: state.capabilities.concat(state.capabilities_other),
      industry_ids: state.industries,
      ...roleTypes,
    };

    if (state.primaryFunction) {
      mom.primary_capabilities_category_id = state.primaryFunction;
    }

    this.save(mom, draft);
  }

  trackEventSubmission() {
    this.props.trackEvents(ANALYTICS.EVENT_TYPES.Success)({
      event: 'Bid Application Submission',
      event_category: 'Talent Apply',
      user_type: 'Mom',
    });
  }

  save(mom, draft) {
    this.setState({ saving: true });
    window.$.ajax({
      url: '/mom/update',
      method: 'PATCH',
      headers: { 'X-CSRF-Token': getCsrfToken() },
      data: { mom },
      complete: data => {
        this.setState({ saving: false });
        if (/20[0-2]/.test(data.status) || draft) {
          /* Proceed with bid submission if updating the mom succeeded or just saving as draft */
          this.trackEventSubmission();
          this.submitBid(draft);
        } else {
          /* Update failed, so show server error messages in a modal */
          const elem = document.createElement('DIV');
          elem.setAttribute('data-confirm-title', 'Save Failed');
          try {
            const serverErrorMessages = Object.values(data.responseJSON)
              .map(errorMessage => {
                return `<p>${errorMessage}</p>`;
              })
              .join('');
            elem.setAttribute('data-confirm', serverErrorMessages);
          } catch (e) {
            elem.setAttribute(
              'data-confirm',
              'An unknown error occurred processing your request. Please check your information and try again.',
            );
          }
          elem.setAttribute('data-confirm-dismiss', 'Close');
          window.$.rails.allowAction(window.$(elem));
          deactivateLoader();
        }
      },
    });
  }

  valid() {
    const {
      primaryFunction,
      capabilities,
      industries,
      phone,
      zip,
      employment_status,
      status,
    } = this.state;
    let errors = false;
    let coverLetter = document.getElementById('bid_proposal_details');

    if (status && status !== 'draft') {
      document.getElementById('submit-error').classList.add('visible');
      return false;
    } else {
      document.getElementById('submit-error').classList.remove('visible');
    }
    if (!primaryFunction) {
      document.getElementById('superpower-message').classList.add('visible');
      errors = true;
    } else {
      document.getElementById('superpower-message').classList.remove('visible');
    }

    if (capabilities.length <= 0 && primaryFunction) {
      document.getElementById('capabilities-message').classList.add('visible');
      errors = true;
    } else if (primaryFunction) {
      document
        .getElementById('capabilities-message')
        .classList.remove('visible');
    }

    if (industries.length <= 0) {
      document.getElementById('industries-message').classList.add('visible');
      errors = true;
    } else {
      document.getElementById('industries-message').classList.remove('visible');
    }
    if (phone.length < 10 || zip.length !== 5) {
      document.getElementById('basic-info-message').classList.add('visible');
      errors = true;
    } else {
      document.getElementById('basic-info-message').classList.remove('visible');
    }
    if (employment_status.length <= 0) {
      document
        .getElementById('employment-status-message')
        .classList.add('visible');
      errors = true;
    } else {
      document
        .getElementById('employment-status-message')
        .classList.remove('visible');
    }

    if (coverLetter.value.length === 0) {
      document.getElementById('missing-why-me').classList.add('visible');
      errors = true;
    } else {
      document.getElementById('missing-why-me').classList.remove('visible');
    }

    if (!window.FileManager.hasValue() || this.willRemoveResume()) {
      document.getElementById('resume-message').classList.add('visible');
      errors = true;
    } else {
      document.getElementById('resume-message').classList.remove('visible');
    }

    if (errors) {
      document.getElementById('see-above').classList.add('visible');
    } else {
      document.getElementById('see-above').classList.remove('visible');
    }
    return errors ? false : true;
  }

  render() {
    const superpowersPresent = this.state.primaryFunction;
    const industries_options = this.getIndustries();
    const capabilitiesMaxLength = Math.max(
      Math.min(
        maxPrimaryCapabilities,
        maxTotalCapabilities - this.state.capabilities_other.length,
      ),
      this.state.capabilities.length,
    );
    const otherCapabilitiesMaxLength = Math.max(
      Math.min(
        maxOtherCapabilities,
        maxTotalCapabilities - this.state.capabilities.length,
      ),
      this.state.capabilities_other.length,
    );
    return (
      <div>
        <div className="bid-detail-layout container pt-3">
          <div className="box box-lg box-shadow-sm pt-4 pl-4 pr-4 pb-3 mb-5">
            <div className="font-bold">Basic Information*</div>
            <div className="row mt-3">
              <div className="col-lg-6 col-md-6 col-12 form-group">
                <label htmlFor="phone-number">Phone Number</label>
                <input
                  id="phone-number"
                  type="text"
                  value={this.state.phone}
                  onChange={e => this.onChange('phone', e.target.value)}
                  className="form-control"
                  placeholder="Phone Number"
                />
              </div>
              <div className="col-lg-6 col-md-6 col-12 form-group">
                <label htmlFor="zipcode">Zip Code</label>
                <input
                  id="zipcode"
                  type="text"
                  value={this.state.zip}
                  onChange={e =>
                    this.onChange('zip', e.target.value.replace(/\D/, ''))
                  }
                  className="form-control"
                  placeholder="Zip Code"
                  maxLength="5"
                  minLength="5"
                  pattern="[0-9]*"
                />
              </div>
            </div>
            <div id="basic-info-message" className="missing-label">
              Missing or Invalid
            </div>
          </div>
          <FunctionsCapabilitiesProvider
            superheroFunctions={this.props.functions}
            capabilities={this.props.capabilities}
            onChange={this.onChangeFunctionsCapabilities}
            selectedSuperheroFunction={this.state.primaryFunction}
            selectedCapabilities={this.state.capabilities}
            selectedOtherCapabilities={this.state.capabilities_other}
            superheroFunctionStateKey="primaryFunction"
            capabilitiesStateKey="capabilities"
            otherCapabilitiesStateKey="capabilities_other"
          >
            <FunctionsCapabilitiesContext.Consumer>
              {({
                getSuperheroFunctionsProps,
                getCapabilitiesProps,
                getOtherCapabilitiesProps,
              }) => (
                <>
                  <div className="box box-lg box-shadow-sm pt-4 pl-4 pr-4 pb-3 mb-5">
                    <div id="superpower-message" className="missing-label">
                      Missing
                    </div>
                    <div className="row mt-3">
                      <div className="col-12 form-group">
                        <TalentSuperheroFunctions
                          {...getSuperheroFunctionsProps({
                            label: 'Your primary superpower*',
                            helpText: 'Select the one that most applies',
                            cols: 3,
                          })}
                        />
                      </div>
                    </div>
                  </div>
                  {superpowersPresent > 0 && (
                    <div className="box box-lg box-shadow-sm pt-4 pl-4 pr-4 pb-3 mb-5">
                      <div id="capabilities-message" className="missing-label">
                        Missing
                      </div>
                      <div className="row mt-3">
                        <div className="col-12 form-group">
                          <TalentCapabilities
                            {...getCapabilitiesProps({
                              cols: 2,
                              label: 'Required Capabilities',
                              maxLength: capabilitiesMaxLength,
                              helpText: 'Select up to 3.',
                            })}
                          />
                          <OtherCapabilities
                            {...getOtherCapabilitiesProps({
                              max: otherCapabilitiesMaxLength,
                            })}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </FunctionsCapabilitiesContext.Consumer>
          </FunctionsCapabilitiesProvider>

          <div className="box box-lg box-shadow-sm pt-4 pl-4 pr-4 pb-3 mb-5">
            <div className="font-bold">
              What industries have you taken by storm?*
              <span className="text-alt font-light pl-3">Max of 6</span>
            </div>
            <div className="mt-3">
              <div className="form-group col">
                <MultiSelect
                  id="industries"
                  defaultValue={this.state.industries}
                  onChange={e => this.onChange('industries', e.target.value)}
                  max={6}
                  keysAccessor={this.keysAccessor}
                  valueAccessor={this.valueAccessor}
                >
                  {industries_options}
                </MultiSelect>
              </div>
            </div>
            <div id="industries-message" className="missing-label">
              Missing
            </div>
          </div>
          <TalentFlexWrapper>
            <div className="box box-lg box-shadow-sm pt-4 pl-4 pr-4 pb-3 mb-1">
              <Questionnaire
                {...employment_status}
                selected={this.state.employment_status}
                cols={2}
                onChange={this.onChange}
                targetName="Employment Status"
              />
              <div id="employment-status-message" className="missing-label">
                Missing
              </div>
            </div>
          </TalentFlexWrapper>
        </div>
        <div className="container">
          <div className="row justify-content-center mt-2 submit-area">
            <div className="col-12 required-label mb-3">
              *You must complete all sections to apply
            </div>
            <div className="bid-bottom border-top pt-5 mb-5 mr-3 ml-3 d-flex align-items-center">
              <div className="col-lg-7 col-md-7 col-12 pl-md-0">
                <div className="box note-box p-4 mb-md-0 cyan-bg">
                  <strong>Note:</strong> You will not be able to edit your
                  application after you submit. Please review your application
                  above before submitting.
                </div>
              </div>
              <div className="col-lg-5 col-md-5 col-12 p-0 text-center">
                {(this.state.status === 'draft' ||
                  this.state.status == null) && (
                  <ButtonLink
                    id="save-draft"
                    type="submit"
                    className="col-12 col-lg-6 col-md-12 mb-2 mb-md-0 tmp-btn-primary" // leaving temporarily for Pendo
                    disabled={this.state.saving}
                    onClick={() => this.handleSubmit(true)}
                    secondary
                    noBorder
                  >
                    Save as Draft
                  </ButtonLink>
                )}
                <Button
                  id="submit-bid"
                  type="submit"
                  className="col-12 col-lg-6 col-md-12 tmp-btn-primary "
                  onClick={() => this.handleSubmit()}
                >
                  {this.state.saving ? 'One moment...' : 'Submit Application'}
                </Button>
                <div id="see-above" className="see-missing-label">
                  See missing info above
                </div>
                <div id="submit-error" className="bg-danger submit-error-label">
                  You cannot submit this bid again
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default BidTalentDetail;
